import type {
  AddOnCodes,
  BillingCycle,
  CompanySizes,
  EstimatedCompanySizes,
} from 'redux/modules/accounts.schema';
import type { Duration } from './units';

/** 0 = Sunday, 1 = Monday, 6 = Saturday */
export type StartOfWeek = 0 | 1 | 6;

/**
 * These flags are temporarily used to support A/B testing or gradual rollouts. They will be removed
 * in the future when a feature is fully rolled out.
 * @see `prereleaseApps` in `src/constants/known-apps.ts`
 */
interface TempFeatures {
  hour_states?: boolean;
  project_templates?: boolean;
  /** Describes if TIC feature is available. Introduced via https://github.com/Timely/timely/pull/3184 as per-workspace opt-in feature for now */
  tic_support?: boolean;
  /**
   * Do we have TIC oAuth/Integrations aka providers available?
   */
  tic_integrations?: boolean;
  /** Google Sheets integration for project sync. Introduced via PR #3809. */
  google_sheets?: boolean;
}

export interface Features extends TempFeatures {
  account_branding?: boolean;
  ai_timesheet_creation?: boolean;
  anomalies?: boolean;
  audits?: boolean;
  batch_log_planned_time?: boolean;
  billing?: boolean;
  branded_reports?: boolean;
  budgets_hourly_rates?: boolean;
  capacity_reports?: boolean;
  company_view?: boolean;
  compliance_dashboard?: boolean;
  control?: boolean;
  custom_project_currencies?: boolean;
  day_locking?: boolean;
  experimental_memory_drawer?: boolean;
  high_level_reports?: boolean;
  in_app_support?: boolean;
  individual_capacity?: boolean;
  integrations?: boolean;
  internal_costs?: boolean;
  invoices?: boolean;
  live_reports?: boolean;
  log_hours_for_others?: boolean;
  memory_retention?: boolean;
  memory_retention_days?: number;
  memory_history_days?: number;
  new_multi_memory_ux?: boolean;
  notifications_project_budget?: boolean;
  people_dashboard?: boolean;
  people_notify?: boolean;
  planned_entries?: boolean;
  planning?: boolean;
  premium_integrations?: boolean;
  project_budget?: boolean;
  project_dashboard?: boolean;
  project_required_fields?: boolean;
  recurring_budget?: boolean;
  tasks?: boolean;
  tasks_reporting?: boolean;
  team_lead?: boolean;
  teams?: boolean | number;
  timesheet_assistant_v2?: boolean;
  weekly_user_capacity?: boolean;
  user_custom_properties?: boolean;
  import_manager?: boolean;
  report_tag_headers?: boolean;
  user_project_distribution?: boolean;
  allow_one_tag?: boolean;
  hide_switch_to_classic?: boolean;
  hide_timestamps_for_others?: boolean;
  all_projects_logging?: boolean;
  client_focus_on_projects?: boolean;
  log_fields_order?: boolean;
}

export interface Logo {
  large_retina: string;
  medium_retina: string;
  small_retina: string;

  /**
   * This flag is set to true by the backend if the account has a custom logo.
   */
  brand_logo?: boolean;
}

/**
 * Extra internal flags only used on the frontend side.
 */
export interface LogoInternal extends Logo {
  delete_logo?: boolean;
}

export interface Currency {
  id: string;
  name: string;
  iso_code: string;
  symbol: string;
  symbol_first: boolean;
}

export enum AccountStatus {
  trial = 'trial',
  trial_expired = 'trial_expired',
  active = 'active',
  exceeded = 'exceeded',
  expired = 'expired',
  on_cancel = 'on_cancel',
}

export interface Experiment {
  name: string;
  value: string;
  platform: string;
}

export interface Account {
  active_projects_count: number;
  appstore_transaction_id: unknown;
  azure_ad_enabled: boolean;
  beta: boolean;
  capacity: Duration;
  color: string;
  company_size: CompanySizes;
  created_at: number;
  currency: Currency;
  days_to_end_trial: number;
  default_hour_rate: number;
  default_work_days: string;
  estimated_company_size: EstimatedCompanySizes;
  experiments: Array<Experiment>;

  /** @deprecated Use `status` instead. */
  expired: boolean;

  features: Features;
  from: string;
  id: number;
  industry: string;
  logo: Logo;
  max_projects: number;
  max_users: number;
  memory_retention_days: number;
  name: string;
  next_charge: string;
  num_projects: number;
  num_users: number;
  owner_id: number;
  paid: boolean;
  payment_mode: string;
  plan_code: string;
  plan_custom: boolean;
  plan_id: number;
  plan_name: string;
  /**
   * Whether one more seat can actually be charged to this account (Stripe customer
   * + usable card + not expired/overdue). Distinguishes "over seats, warn and
   * charge them" from "over seats, block and talk to your account manager".
   *
   * Optional because an API deployed before Timely/timely#4240 doesn't send it.
   * Only an explicit `false` blocks — see `getHasReachedUserLimit`.
   */
  can_purchase_extra_seats?: boolean;
  seats: number;
  start_of_week: StartOfWeek;
  status: AccountStatus;
  support_email: string;
  total_projects_count: number;

  /** @deprecated Use `status` instead. */
  trial: boolean;

  weekly_user_capacity: number;
  tic_force_enable?: boolean;
}

export type FeaturesResponse = Omit<Features, 'memory_history_days'> & { memories?: boolean };

export interface FeaturesPayload {
  name: keyof FeaturesResponse;
  days: number;
}

export type AccountResponse = Omit<Account, 'features'> & { features: Array<FeaturesPayload> };

export interface SubscriptionPlan {
  billing_cycle: BillingCycle;
  campaign?: string;
  code: string;
  id: number;
  name: string;
  price: number;
  price_monthly: number;
  box_features: Array<BoxFeature>;
}

/**
 * Fake plan used to emulate old subscription plans.
 */
export const EMULATED_OLD_SUBSCRIPTION_PLAN: SubscriptionPlan = {
  billing_cycle: 'monthly',
  code: '--EMULATED_OLD_SUBSCRIPTION_PLAN--',
  id: -1,
  name: '--EMULATED_OLD_SUBSCRIPTION_PLAN--',
  price: 1,
  price_monthly: 1,
  box_features: [],
};

export interface BoxFeature {
  id: number;
  plan_id: number;
  name: string;
  tooltip: string;
  header: boolean;
  strike: boolean;
}

export type AddOnNames = 'Tasks';

export interface RawAddOn {
  id: number;
  code: AddOnCodes;
  name: AddOnNames;
  campaign: string;
  free: boolean;
  price: number;
  price_monthly: number;
  extra_user_price: number;
  extra_user_price_monthly: number;
  users_included: number;
  users_limit: number;
  projects_limit: number;
  billing_cycle: BillingCycle;
  target: string;
  short_description: string | null;
  visual_settings: string | null;
  box_features: Array<BoxFeature>;
  next_charge: string;
  status: AccountStatus | null;
  days_in_trial: number;
}

export interface AccountUserProperty {
  id: number;
  name: string;
}

interface DeleteAccountUserProperty {
  id: number;
  _destroy: 'true';
}

interface NewAccountUserProperty {
  name: string;
}

export type ClientSideAccountUserProperty = (
  | AccountUserProperty
  | DeleteAccountUserProperty
  | NewAccountUserProperty
) & { _clientSideId: number };

export type UserAccountProperty = {
  account_user_property_id: number;
  id: number;
  user_account_id: number;
  value: string;
};
